/**
 * Formatting helpers for the report-pickup pages.
 *
 * A plain `.ts` module, not `.tsx`, for the same reason `status.ts` is: the eslint config runs
 * `react-refresh/only-export-components` as a warning and lint runs with `--max-warnings 0`, so
 * exporting a function from a file that also exports components fails the build.
 */

import type { SubscriptionPickupState } from '@/types';

/**
 * File size in the units people actually say out loud. Uses 1024 rather than 1000 because it has
 * to agree with what Windows Explorer shows for the same file once it is downloaded.
 */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  // No decimal on bytes and kilobytes — "1.0 KB" reads as false precision for a rounded figure.
  const decimals = exponent === 0 ? 0 : value >= 100 || exponent === 1 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[exponent]}`;
}

/**
 * Row count, or an em dash when it is unknown.
 *
 * Null and zero are deliberately different: null means the file could not be matched to a run in
 * the report server's execution log, while 0 means the report genuinely returned nothing — which
 * is a real and important answer, because an empty report is usually a problem worth seeing.
 */
export function formatRows(rows: number | null | undefined): string {
  if (rows === null || rows === undefined) return '—';
  return rows.toLocaleString();
}

/**
 * `yyyy-MM-dd` bounds for a whole calendar month, offset from the current one.
 * `monthRange(-1)` is last month, which is the page's default journey.
 */
export function monthRange(monthOffset: number): [string, string] {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + monthOffset + 1, 0);
  return [toDateInput(start), toDateInput(end)];
}

/** Local-time `yyyy-MM-dd`. `toISOString` would shift the date across midnight for anyone east of UTC. */
function toDateInput(d: Date): string {
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * How to describe whose view of the data a file is.
 *
 * This is not decoration. A KT Insights export renders AS ITS RECIPIENT — a row-level filter is
 * bound to that person's email for every role except Internal, and per-user page permissions
 * decide what the file contains at all. Two files from the same preset, period and client are
 * therefore genuinely different documents, and somebody about to forward one to a customer needs
 * to know which one they have.
 *
 * The unknown case is stated rather than left blank, because a blank cell reads as "not
 * applicable" when what it actually means is "nobody checked".
 */
export function describeRenderIdentity(file: {
  producer: 'Pbirs' | 'KtInsights';
  renderedAsEmail?: string | null;
  renderedAsName?: string | null;
  renderedAsRoleName?: string | null;
}): { text: string; detail: string; tone: 'internal' | 'external' | 'none' | 'unknown' } {
  if (file.producer === 'Pbirs') {
    return {
      text: 'Report server',
      detail:
        'Rendered by the report server under its own account. The data is scoped by the '
        + 'subscription’s report parameters, not by any person’s permissions.',
      tone: 'none'
    };
  }

  if (!file.renderedAsEmail) {
    return {
      text: 'Unknown',
      detail:
        'This export renders as its recipient, and the portal could not resolve who that was — '
        + 'so what this file contains cannot be confirmed. Check before sharing it.',
      tone: 'unknown'
    };
  }

  const who = file.renderedAsName?.trim() || file.renderedAsEmail;
  const isInternal = file.renderedAsRoleName === 'Internal';

  return {
    text: `${who}${file.renderedAsRoleName ? ` — ${file.renderedAsRoleName}` : ''}`,
    detail: isInternal
      ? `Rendered as ${file.renderedAsEmail}, a Key Travel user — this file contains the full internal view of the data.`
      : `Rendered as ${file.renderedAsEmail}. KT Insights restricted the data to that person’s own `
        + 'organisation and to the report pages they are permitted to see.',
    tone: isInternal ? 'internal' : 'external'
  };
}

/** Which year folder a file is in, for the detail panel. Nothing ever moves between them. */
export function describeLocation(yearFolder: number | null | undefined): string {
  return yearFolder ? `${yearFolder} folder` : 'No year folder';
}

/**
 * The year folder, but only when it disagrees with the period the file covers.
 *
 * The folder records the year the RUN fired and the period records what the report covers, and
 * for all but a handful of files a year those are the same number — showing it every time would
 * put "2026" next to "July 2026" on every row. The disagreement is the interesting case: a
 * December export firing on 1 January is filed under the new year, and somebody who cannot find
 * last month's file where they expect it needs to be told that once, on the row.
 */
export function describeStraddledYear(
  yearFolder: number | null | undefined,
  periodKey: string | null | undefined
): string | null {
  if (!yearFolder || !periodKey) return null;
  return periodKey.startsWith(`${yearFolder}`) ? null : `Filed under ${yearFolder}`;
}

/**
 * Where a subscription's output goes, in the words the list and the detail page both use.
 *
 * One helper for both so they cannot drift apart — a subscription described as "Email + Pickup"
 * in the list and "Email only" when opened would be worse than either being wrong on its own.
 *
 * The healthy answer is deliberately plain: almost every subscription is email-only, and the point
 * of the column is that the handful which are not stand out without anyone having to open them.
 */
export function describeDelivery(pickup?: SubscriptionPickupState | null): {
  label: string;
  /** Maps onto the existing status-pill classes, so the list needs no new visual vocabulary. */
  tone: 'success' | 'failed' | 'pending' | 'unknown' | 'none';
  detail: string | null;
} {
  if (!pickup || pickup.health === 'None') {
    return { label: 'Email', tone: 'none', detail: null };
  }

  switch (pickup.health) {
    case 'Paired':
      return {
        label: 'Email + Pickup',
        tone: 'success',
        detail: pickup.clientName
          ? `Filed under ${pickup.clientName}.`
          : 'Filed to report pickup as well as emailed.'
      };

    case 'CompanionMissing':
      return {
        label: 'Pickup broken',
        tone: 'failed',
        detail: pickup.driftDetail
          ?? 'The file-share subscription that writes the file is no longer on the report server.'
      };

    case 'CompanionDisabled':
      return {
        label: 'Pickup stopped',
        tone: 'failed',
        detail: pickup.driftDetail
          ?? 'The file-share subscription has been disabled, so the email goes out but no file is written.'
      };

    case 'CompanionDiverged':
      return {
        label: 'Pickup differs',
        tone: 'pending',
        detail: pickup.driftDetail
          ?? 'The file-share subscription no longer matches the email one, so the file and the email are not the same document.'
      };

    // Not folded in with the healthy case on purpose: "I could not check" and "I checked and it is
    // fine" are different answers, and only one of them should look reassuring.
    case 'Unknown':
    default:
      return {
        label: 'Email + Pickup',
        tone: 'unknown',
        detail: 'The report server could not be reached to confirm the pickup delivery is still set up.'
      };
  }
}

/**
 * Triggers a browser download for a Blob already fetched with the user's bearer token.
 *
 * The object URL is revoked on the next tick rather than immediately: revoking synchronously
 * races the browser's own read of the URL and produces an empty file in some versions.
 */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
